import React, { useState, useEffect } from 'react';
import Divider from '@mui/material/Divider';
import FormGroup from '@mui/material/FormGroup';
import TR from '../../Trans';
import MenuItem from '@mui/material/MenuItem';
import ContentContainer from '../items/ContentContainer';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import OutlinedInput from '@mui/material/OutlinedInput';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Waiting from '../items/Waiting';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import PinQuestion from '../dialogs/PinQuestion';
import { Views } from './../../enums/enums';
import navigationStore from '../../stores/navigation';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import environmentStore from '../../stores/environment';
import accountStore from '../../stores/account';
import sessionStore from '../../stores/session';
import messageStore from '../../stores/message';
import settingsStore from '../../stores/settings';
import { setUserAgent } from 'react-device-detect';

const pinAttemptTimeout = 60 * 1000;

function ChangeUsername() {

  const goToView = navigationStore((state) => state.goToView);
  const sessionToken = sessionStore((state) => state.sessionToken);
  const account = accountStore((state) => state.account);
  const changeAccount = accountStore((state) => state.changeAccount);
  const getAccount = accountStore((state) => state.getAccount);
  const setError = messageStore((state) => state.setError);
  const settings = settingsStore((state) => state.settings);
  const getSettings = settingsStore((state) => state.getSettings);
  const isAccountPinSet = accountStore((state) => state.isAccountPinSet);
  const verifyPin = accountStore((state) => state.verifyPin);
  const verificationCode = accountStore((state) => state.verificationCode);
  const createAccountPin = accountStore((state) => state.createAccountPin);
  const verifyCurrentPassword = accountStore((state) => state.verifyCurrentPassword);
  const changeUsername = accountStore((state) => state.changeUsername);
  const clearVerifications = accountStore((state) => state.clearVerifications);
  const verifyPinChangeUsername = accountStore((state) => state.verifyPinChangeUsername);
  const isWaiting = accountStore((state) => state.isWaiting);

  const [newUsername, setNewUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [isPinQuestionOpen, setIsPinQuestionOpen] = useState(true);
  const [isPinChecked, setIsPinChecked] = useState(false);

  const [wrongPinAttempts, setWrongPinAttempts] = useState(0);
  const timeoutPinAttempts = () => {
    setTimeout(() => setWrongPinAttempts(0), pinAttemptTimeout);
  };

  useEffect(() => {
    if (!settings) {
      getSettings(sessionToken);
    }
    getAccount(sessionToken);
    clearVerifications();
  }, []);

  useEffect(() => {
    if (account) {
      setNewUsername("");
      setPassword("");
    }
  }, [account]);

  const handleCheckPin = (pin) => {
    if (pin === settings.account_pin) {
      setIsPinChecked(true);
    } else {
      if (wrongPinAttempts == 0) {
        timeoutPinAttempts();
      }
      if (wrongPinAttempts >= 4) {
        setError(sessionToken, 0, TR.cm_too_many_failed_attempts_try_later);
      } else {
        setError(sessionToken, 0, TR.cm_invalid_pin);
      }
      setWrongPinAttempts(wrongPinAttempts + 1);
    }
  };

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleVerifyCurrentPassword = () => {
    if (newUsername.length == 0) {
      setError(sessionToken, 0, TR.cm_username_cannot_be_empty);
      return;
    }
    if (!validateEmail(newUsername)) {
      setError(sessionToken, 0, TR.cm_invalid_email);
      return;
    }
    if (password.length == 0) {
      setError(sessionToken, 0, TR.cm_bad_username_or_pw);
      return;
    }
    verifyCurrentPassword(sessionToken, account.username, newUsername, password);
  };

  const handleOpenPinQuestion = () => {
    setIsPinQuestionOpen(true);
  };

  const handleClosePinQuestion = () => {
    setIsPinQuestionOpen(false);
    goToView(Views.Home);
  };

  const renderEmailForm = () => {
    if (isWaiting) {
      return <Waiting />;
    } else {
      return <div>
        <InputLabel>{TR.cm_current_password}</InputLabel>
        <OutlinedInput
          id="change-username-password"
          margin="normal"
          required
          fullWidth
          size="small"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type={showPassword ? 'text' : 'password'}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                onMouseDown={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <InputLabel>{TR.cm_new_username_email}</InputLabel>
        <TextField
          size="small"
          required
          fullWidth
          type="text"
          value={newUsername}
          onChange={(e) => setNewUsername(e.target.value.toLowerCase())}
        />
        <Button
          onClick={() => {
            handleVerifyCurrentPassword();
          }}
          fullWidth
          color="success"
          variant="text"
          sx={{ mt: 3, mb: 2 }}
        >
          {TR.cm_next}
        </Button>
      </div>;
    }
  };

  const renderForm = () => {
    if (account) {
      if (!isAccountPinSet) {
        return renderEmailForm();
      } else {
        return <Box component="form" noValidate sx={{ mt: 1 }}>
          <p>
            {TR.cm_account_creation_pin_sent}
          </p>
          <InputLabel>Pin</InputLabel>
          <TextField
            margin="normal"
            required
            fullWidth
            type="text"
            size="small"
            autoComplete="off"
            onChange={(e) => setPin(e.target.value)}
          />
          <Button
            onClick={() => {
              verifyPinChangeUsername(sessionToken, pin, newUsername, "change_username", newUsername, password);
            }}
            fullWidth
            color="success"
            variant="text"
            sx={{ mt: 3, mb: 2 }}
          >
            {TR.cm_next}
          </Button>
        </Box>;
      }
    }
  };

  const renderPinDialogOrForm = () => {
    if (settings) {
      if (settings.account_pin && !isPinChecked) {
        return <PinQuestion open={isPinQuestionOpen} onClose={handleClosePinQuestion} onCheckPin={handleCheckPin} />;
      } else {
        return <div>
          <h3>{TR.cm_change_username}</h3>
          {renderForm()}
        </div>;
      }
    }
  };

  return (
    <div>
      {renderPinDialogOrForm()}
    </div>
  );
}

export default ChangeUsername;